<template>
  <AdminLayout>
    <Head title="Balance Audit" />

    <template #sectionTitle> {{ product.name }} - Audit </template>

    <div class="headline flex flex-col md:flex-row justify-between items-start md:items-center mx-4 sm:mx-12">
      <h2 class="text-2xl font-semibold text-blue-600">
        <Link
          v-if="package"
          :href="packageLink"
          class="font-semibold text-blue-600 hover:text-blue-600"
        >
          {{ package.data.name }}
        </Link>
        <Link
          v-else
          :href="productLink"
          class="font-semibold text-blue-600 hover:text-blue-600"
        >
          {{ product.name }}
        </Link>
        <span> - Balance Audit</span>
      </h2>

      <div class="relative inline-flex self-start my-5">
        <Menu
          as="div"
          class="relative mr-2 text-left"
          v-if="showingActionButtons"
        >
          <div>
            <MenuButton
              class="inline-flex justify-center w-full rounded-md border border-gray-400 shadow-sm px-4 py-2 bg-gray-100 text-sm font-medium text-gray-700 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-400 focus:ring-blue-600"
            >
              Actions
              <ChevronDownIcon
                class="-mr-1 ml-2 h-5 w-5"
                aria-hidden="true"
              />
            </MenuButton>
          </div>

          <transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="opacity-0 scale-95"
            enter-to-class="opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="opacity-100 scale-100"
            leave-to-class="opacity-0 scale-95"
          >
            <MenuItems
              class="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-gray-100 ring-1 ring-gray-400 ring-opacity-5 divide-y divide-gray-400 focus:outline-none z-50"
            >
              <div class="py-1">
                <MenuItem v-slot="{ active }">
                  <a
                    @click.prevent="returnBalanceEntriesWithOrders()"
                    href="#"
                    :class="[
                      active ? 'bg-gray-400 text-gray-900' : 'text-gray-700',
                      'group flex items-center px-4 py-2 text-sm hover:bg-blue-600 hover:text-gray-100'
                    ]"
                  >
                    Return Orders
                  </a>
                </MenuItem>
              </div>
            </MenuItems>
          </transition>
        </Menu>

        <button
          @click="download()"
          type="button"
          class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-gray-100 bg-blue-600 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600"
        >
          Download
        </button>
      </div>
    </div>

    <div class="relative inline-flex self-start content-center my-5 mx-4 sm:mx-12">
      <div class="input-group mr-2">
        <select
          v-model="form.location_id"
          id="product-regulated"
          class="bg-gray-100"
        >
          <option :value="null">All Locations</option>
          <option
            v-for="location in sortedLocations"
            :key="location.id"
            :value="location.id"
          >
            {{ location.name }}
          </option>
        </select>
      </div>

      <div class="input-group mr-2">
        <select
          v-model="form.range"
          id="product-regulated"
          class="bg-gray-100"
        >
          <option
            v-for="(value, key) in ranges"
            :key="key"
            :value="key"
          >
            {{ value }}
          </option>
        </select>
      </div>

      <div
        v-if="form.range === 'custom'"
        class="mr-2"
      >
        <div class="input-group">
          <date-range-picker
            v-model:start="form.start"
            v-model:end="form.end"
          />
        </div>
      </div>

      <div class="input-group-checkbox items-center mr-4">
        <div class="input-checkbox-container">
          <input
            v-model="form.options.show_returnable_orders"
            id="show_returnable_orders"
            name="show_returnable_orders"
            type="checkbox"
          />
        </div>
        <div class="input-label-container">
          <label for="show_returnable_orders"> Returnable Orders </label>
        </div>
      </div>

      <div class="input-group-checkbox items-center">
        <div class="input-checkbox-container">
          <input
            v-model="form.options.show_reserved_orders"
            id="show_reserved_orders"
            name="show_reserved_orders"
            type="checkbox"
          />
        </div>
        <div class="input-label-container">
          <label for="show_reserved_orders"> Reserved Orders </label>
        </div>
      </div>
    </div>

    <data-table
      :items="entries.data"
      :headers="headers"
      :actions="[]"
      v-model="selected"
    >
      <template v-slot:rows="slotRow">
        <index-row
          v-slot:rows="slotRow"
          v-bind="slotRow"
        />
      </template>
    </data-table>

    <Pagination
      :page="entries.meta.current_page"
      :total="entries.meta.total"
      :per-page="entries.meta.per_page"
      :path="entries.meta.path"
      :params="params"
      v-if="entries.meta.last_page > 1"
    />

    <template #modals></template>
  </AdminLayout>
</template>

<script>
import { Head, Link } from '@inertiajs/vue3'
import { ChevronDownIcon } from '@heroicons/vue/solid'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import AdminLayout from '@/Layouts/AdminLayout.vue'
import DataTable from '@/Components/Table/DataTable'
import IndexRow from '@/Pages/Products/BalanceEntries/Components/IndexRow'
import Pagination from '@/Components/Pagination.vue'
import DateRangePicker from '@/Components/Forms/DateRangePicker'
import formatters from '@/Mixins/formatters'
import isUrlAware from '@/Mixins/isUrlAware'
import _date from '@/Filters/Date'
import interactsWithCurrentLocation from '@/Mixins/interactsWithCurrentLocation'
import { isOrderEntry } from '@/Pages/Products/BalanceEntries/Filters/BalanceEntries'

const trashedOptions = [
  { title: 'Active', current: true },
  { title: 'Archived', current: false }
]

export default {
  mixins: [formatters, isUrlAware, interactsWithCurrentLocation],

  components: {
    AdminLayout,
    Head,
    Link,
    DataTable,
    IndexRow,
    Pagination,
    DateRangePicker,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    ChevronDownIcon
  },

  props: {
    /**
     * Query filter params.
     */
    params: Object,
    /**
     * Available date ranges to select.
     */
    ranges: Array,
    /**
     * Data object with packages audited
     */
    packages: Object,
    /**
     * The product being audited
     */
    product: Object,
    /**
     * The package being audited
     */
    package: {
      type: Object,
      default: null
    },
    /**
     * Balance entries for all packages
     */
    entries: Object
  },

  data() {
    return {
      form: this.$inertia.form({
        package_id: this.params.package_id || null,
        location_id: this.params.location_id || null,
        start: this.params.start || null,
        end: this.params.end || null,
        range: this.params.range || null,
        options: this.params.options || {}
        // show_returnable_orders: this.params.show_returnable_orders || false,
        // show_reserved_orders: this.params.show_reserved_orders || false,
      }),
      selected: [],
      returnForm: this.$inertia.form({
        orders: []
      })
    }
  },

  watch: {
    'form.location_id': function (newVal, oldVal) {
      this.update()
    },
    'form.range': function (newVal, oldVal) {
      if (newVal !== 'custom') {
        this.form.start = this.form.end = null
        this.update()
      }
    },
    'form.start': function (newVal, oldVal) {
      if (this.form.start && this.form.end) {
        this.update()
      }
    },
    'form.end': function (newVal, oldVal) {
      if (this.form.start && this.form.end) {
        this.update()
      }
    },
    'form.options.show_returnable_orders': function (newVal, oldVal) {
      this.update()
    },
    'form.options.show_reserved_orders': function (newVal, oldVal) {
      this.update()
    }
  },

  computed: {
    showingActionButtons() {
      return this.selected.length > 0
    },
    existingEntries() {
      return this.entries.data.filter((item) => {
        return !item.deleted_at
      })
    },
    headers() {
      return [
        'ID',
        'Package',
        'Location',
        'Type',
        'Causer',
        'Person',
        'Movement',
        'Remaining',
        'Reason',
        'Date',
        'Updated',
        'Deleted'
      ]
    },
    actions() {
      return ['dispense']
    },
    productLink() {
      return route('products.edit', { product: this.product.id })
    },
    packageLink() {
      return route('packages.show', { package: this.package.data.id })
    }
  },

  methods: {
    _date,
    isOrderEntry,

    update() {
      this.form.get(route('products.balance-entries.index', { product: this.product.id }))
    },
    download() {
      window.open(
        route('products.balance-entries.index', { product: this.product.id, ...this.form, download: true }),
        '_blank'
      )
    },
    returnBalanceEntriesWithOrders() {
      let entries = this.selected

      entries = _.filter(entries, (entry) => {
        return isOrderEntry(entry)
      })

      this.returnForm.orders = _.map(entries, (entry) => {
        return entry.causer.order_id
      })

      this.returnForm.post(this.route('orders.bulk-return'), {
        onFinish: () => this.returnForm.reset()
      })
    }
  }
}
</script>
